import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
// Utils
import { showToast } from './ToastsUtil';
import { SERVER_COMPUTED_PROPERTIES } from './offline/MergeUtil';
import Connectivity from './offline/Connectivity';

// Valeurs du champ « entity » du message « Changed », identiques à api/api/Models/Enumerators/RealtimeEntity.cs
export const REALTIME_ENTITIES = {
    elements: 'elements', elementHistories: 'elementHistories', actionHistories: 'actionHistories',
    actionOccurrences: 'actionOccurrences', actionSeries: 'actionSeries', projectEvents: 'projectEvents', projectEventElements: 'projectEventElements',
    fileInfos: 'fileInfos', tags: 'tags', project: 'project', baseProjects: 'baseProjects', userBaseProjects: 'userBaseProjects',
    projectRoles: 'projectRoles', filters: 'filters', customCharts: 'customCharts', projectLogs: 'projectLogs', stationElements: 'stationElements',
    organization: 'organization', userOrganizations: 'userOrganizations', organizationInvitations: 'organizationInvitations', themes: 'themes',
    customFields: 'customFields', customFieldCategories: 'customFieldCategories', priceLists: 'priceLists',
    backgroundTasks: 'backgroundTasks', importInfos: 'importInfos'
};

// Date ISO de l'API, avec ou sans fuseau
const ISO_DATE = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(Z|[+-]\d\d:\d\d)?$/i;
const EPHEMERAL_EVENTS = ['PresenceChanged', 'LocksChanged', 'FormMembersChanged', 'FormUpdated', 'FormStateRequested'];
// La politique par défaut de SignalR abandonne après 4 essais (~42 s) : ici on réessaie indéfiniment
const RETRY_DELAYS = [0, 2000, 5000, 10000, 30000];
const HEARTBEAT_INTERVAL = 30000;
// Qualité de la connexion, envoyée avec le battement et affichée dans la liste des collaborateurs connectés
const LATENCY_SAMPLES = 3; // médiane des dernières latences mesurées
const GOOD_LATENCY = 300;
const POOR_LATENCY = 1000;
const DISCONNECTIONS_WINDOW = 10 * 60 * 1000;
// Stateful reconnect (identique à StatefulReconnectBufferSize côté API) : une coupure brève du transport reprend la même connexion
// et rejoue les messages manqués, sans passer par « reconnecting ». Une seule tentative : si elle échoue, reconnexion classique
const STATEFUL_RECONNECT_BUFFER_SIZE = 1024 * 1024;
// Au-delà de cette durée de coupure, des messages ont pu être perdus : évènement « resynced »
const RESYNC_THRESHOLD = 5000;

// Connexion unique au hub /hubs/realtime, partagée par tout l'onglet.
// Les handlers SignalR sont branchés une seule fois à la création de la connexion et redistribués aux abonnés :
// une reconnexion ne les duplique pas. Les projets et formulaires rejoints sont rejoués à chaque (re)connexion.
export default class RealtimeUtil {
    static connection = null;
    static token = null;
    static subscriptions = {};
    static listeners = {};
    static projects = new Map(); // projectId → { count, deviceInfos }
    static forms = new Map(); // `${projectId}:${formKey}` → { count, projectId, formKey }
    static remoteFeatures = new WeakSet(); // Versions d'éléments reçues du serveur (ProjectWebSocket), seules fusionnées par les formulaires ouverts
    static syncedFeatures = new WeakSet(); // Versions serveur de ses propres saisies acceptées en retard
    static formBaselines = new Map(); // `${projectId}:${formKey}` → état déjà partagé avec les autres membres (référence des différences)
    static presence = new Map(); // projectId → derniers membres connus (pour un composant monté après JoinProject)
    static heartbeat = null;
    static latencies = [];
    static disconnections = []; // dates des coupures récentes
    static retryTimeout = null;
    static disconnectedAt = null;
    static hasBeenConnected = false;
    static isInitialized = false;

    // Appelé une fois par App : intercepteur Axios, écoute du réseau, première connexion
    static init() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        // Exclusion de l'auteur : l'API ne renvoie pas une modification à la connexion qui l'a faite
        Axios.interceptors.request.use(config => {
            const connectionId = this.getConnectionId();
            if (connectionId && config.url?.startsWith(AppSettings.getBackendUrl()))
                config.headers = { ...config.headers, 'X-Connection-Id': connectionId };
            return config;
        });

        window.addEventListener('online', () => this.retryNow());
        window.addEventListener('offline', () => this.stop());
        // Retour au premier plan (tablette sortie de veille, appli rouverte) : le réseau a pu revenir sans évènement « online »
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.retryNow();
        });
        this.start();
    }

    static getToken = () => new Cookies().get('token') || null;
    static getConnectionId = () => this.connection?.state === HubConnectionState.Connected ? this.connection.connectionId : null;
    static isConnected = () => this.connection?.state === HubConnectionState.Connected;

    static start() {
        if (!navigator.onLine) return;
        if (this.connection) return this.connect();

        this.token = this.getToken();
        const options = this.token ? { accessTokenFactory: () => this.getToken() } : {};
        const connection = new HubConnectionBuilder()
            .withUrl(`${AppSettings.getBackendUrl()}hubs/realtime`, options)
            .withAutomaticReconnect({ nextRetryDelayInMilliseconds: ({ previousRetryCount }) => RETRY_DELAYS[Math.min(previousRetryCount, RETRY_DELAYS.length - 1)] })
            .withStatefulReconnect({ bufferSize: STATEFUL_RECONNECT_BUFFER_SIZE })
            .build();
        this.connection = connection;
        // Rechargement ou fermeture de l'onglet : fin propre, sinon le serveur garde la connexion (et sa présence) le temps d'une reprise stateful
        if (!this.handlePageHide) {
            this.handlePageHide = () => this.connection?.stop();
            window.addEventListener('pagehide', this.handlePageHide);
        }

        connection.on('Changed', this.handleChanged);
        EPHEMERAL_EVENTS.forEach(event => connection.on(event, (...args) => this.emit(event, ...args)));
        connection.onreconnecting(() => {
            // Connexion perdue sans requête en cours : la sonde vérifie si l'API reste joignable
            Connectivity.probe();
            this.setDisconnected();
            this.stopHeartbeat();
            this.emit('reconnecting');
        });
        connection.onreconnected(() => this.handleConnected());
        connection.onclose(() => {
            this.stopHeartbeat();
            if (this.connection !== connection) return; // Connexion remplacée (stop, restart)
            this.setDisconnected();
            this.scheduleConnect();
            this.emit('closed');
        });

        return this.connect();
    }

    static connect() {
        const connection = this.connection;
        if (!connection || connection.state !== HubConnectionState.Disconnected) return Promise.resolve();
        // Un échec du premier start() n'est pas couvert par la reconnexion automatique
        return connection.start().then(() => {
            if (this.connection === connection) this.handleConnected();
        }).catch(() => {
            if (this.connection !== connection) return;
            if (this.hasBeenConnected) this.setDisconnected();
            else if (!this.disconnectedAt) this.disconnectedAt = Date.now();
            this.scheduleConnect();
        });
    }

    // Nouvel essai immédiat au lieu d'attendre le prochain (jusqu'à 30 s) : connexion coupée ou en cours de reconnexion seulement
    static retryNow() {
        const connection = this.connection;
        if (!connection) return this.start();
        if (connection.state === HubConnectionState.Disconnected) {
            clearTimeout(this.retryTimeout);
            return this.connect();
        }
        // La reconnexion automatique de SignalR ne peut pas être avancée : on la remplace par une nouvelle connexion
        if (connection.state === HubConnectionState.Reconnecting) {
            this.stop();
            return this.start();
        }
    }

    static scheduleConnect() {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = setTimeout(() => this.connect(), RETRY_DELAYS[RETRY_DELAYS.length - 1]);
    }

    static stop() {
        clearTimeout(this.retryTimeout);
        this.stopHeartbeat();
        const connection = this.connection;
        this.connection = null;
        if (connection) {
            if (this.hasBeenConnected) this.setDisconnected();
            return connection.stop().catch(() => { });
        }
        return Promise.resolve();
    }

    // Connexion ou déconnexion de l'utilisateur : les groupes dépendent du jeton
    static restart() {
        if (this.connection && this.getToken() === this.token) return;
        this.stop();
        // Autre utilisateur : ses mesures de qualité repartent de zéro
        this.latencies = [];
        this.disconnections = [];
        this.start();
    }

    static setDisconnected() {
        if (this.disconnectedAt) return;
        this.disconnectedAt = Date.now();
        this.disconnections.push(this.disconnectedAt);
    }

    static handleConnected() {
        const shouldResync = this.hasBeenConnected && this.disconnectedAt && Date.now() - this.disconnectedAt > RESYNC_THRESHOLD;
        this.hasBeenConnected = true;
        this.disconnectedAt = null;

        clearInterval(this.heartbeat);
        this.heartbeat = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL);

        this.projects.forEach(({ deviceInfos }, projectId) => this.invokeJoinProject(projectId, deviceInfos));
        this.forms.forEach(({ projectId, formKey }) => this.invokeJoinForm(projectId, formKey));
        this.sendHeartbeat(); // Première mesure ; chaque JoinProject en renvoie une pour son projet

        this.emit('connected');
        if (shouldResync) this.emit('resynced');
    }

    // Un seul appel par battement : il envoie la qualité mesurée jusque-là, et son aller-retour sert de mesure pour le suivant.
    // Sans mesure (connexion), un premier battement sans qualité mesure la liaison avant d'envoyer la qualité
    static async sendHeartbeat() {
        const connection = this.connection;
        if (!this.isConnected()) return;
        if (!this.latencies.length && !(await this.invokeHeartbeat(connection, null))) return;
        if (this.connection === connection && this.isConnected()) this.invokeHeartbeat(connection, this.getQuality());
    }

    // Mesurée aussi en arrière-plan : le navigateur espace alors les battements, mais l'aller-retour lui-même n'est pas ralenti
    static async invokeHeartbeat(connection, quality) {
        const start = performance.now();
        try { await connection.invoke('Heartbeat', quality); }
        catch { return false; }
        if (this.connection === connection) this.latencies = [...this.latencies, Math.round(performance.now() - start)].slice(-LATENCY_SAMPLES);
        return true;
    }

    // Niveau « good », « medium » ou « poor » ; null tant qu'aucune latence n'a été mesurée
    static getQuality() {
        const now = Date.now();
        this.disconnections = this.disconnections.filter(date => now - date < DISCONNECTIONS_WINDOW);
        if (!this.latencies.length) return null;

        const sortedLatencies = [...this.latencies].sort((a, b) => a - b);
        const latency = sortedLatencies[Math.floor(sortedLatencies.length / 2)];
        const disconnections = this.disconnections.length;
        const networkType = navigator.connection?.effectiveType || null; // Chrome et Android seulement
        const level = latency > POOR_LATENCY || disconnections >= 2 || ['2g', 'slow-2g'].includes(networkType) ? 'poor'
            : latency < GOOD_LATENCY && !disconnections ? 'good'
                : 'medium';
        return { level, latency, disconnections, networkType };
    }

    static stopHeartbeat() {
        clearInterval(this.heartbeat);
        this.heartbeat = null;
    }

    static handleChanged = (json) => {
        let message;
        try { message = JSON.parse(json); } catch { return; }
        // Message provoqué par cet onglet
        if (message.origin && message.origin === this.connection?.connectionId) return;
        this.dispatch(message);
    }

    // Distribue un message { entity, op, projectId, data } aux abonnés ; sert aussi à rejouer des données relues par REST
    static dispatch(message) {
        (this.subscriptions[message.entity] || new Set()).forEach(handler => {
            try { handler(message.op, message.data, message); }
            catch (e) { console.error(e); }
        });
    }

    // Abonnement à une entité du message « Changed » : handler(op, data, message). Renvoie la fonction de désabonnement.
    static subscribe(entity, handler) {
        if (!this.subscriptions[entity]) this.subscriptions[entity] = new Set();
        this.subscriptions[entity].add(handler);
        return () => this.subscriptions[entity].delete(handler);
    }

    // Raccourci pour les éléments : handler(elements) pour « created » et « updated », handler(category, ids) pour « removed »
    static subscribeElements(op, handler) {
        return this.subscribe(REALTIME_ENTITIES.elements, (messageOp, data) => {
            if (messageOp !== op) return;
            if (op === 'removed') handler(data.category, data.ids);
            else handler(data);
        });
    }

    // Messages éphémères (PresenceChanged, LocksChanged, FormMembersChanged, FormUpdated, FormStateRequested)
    // et état de la connexion (connected, reconnecting, closed, resynced). Renvoie la fonction de désabonnement.
    static on(event, handler) {
        if (!this.listeners[event]) this.listeners[event] = new Set();
        this.listeners[event].add(handler);
        return () => this.listeners[event].delete(handler);
    }

    static emit(event, ...args) {
        if (event === 'PresenceChanged') this.presence.set(args[0], args[1] || []);
        // Ce que les autres ont envoyé est connu de tous : on ne le renverra pas
        if (event === 'FormUpdated') {
            const key = `${args[0]}:${args[1]}`;
            const baseline = this.formBaselines.get(key);
            if (baseline) try { this.formBaselines.set(key, this.deepMerge(baseline, JSON.parse(args[2]))); } catch { this.formBaselines.delete(key); }
        }
        (this.listeners[event] || new Set()).forEach(handler => {
            try { handler(...args); }
            catch (e) { console.error(e); }
        });
    }

    static invoke(method, ...args) {
        if (!this.isConnected()) return Promise.resolve(null);
        return this.connection.invoke(method, ...args).catch(error => {
            console.error(error);
            return null;
        });
    }

    // Projet : présence et verrous arrivent par PresenceChanged et LocksChanged, y compris pour l'appelant

    static joinProject(projectId, deviceInfos = null) {
        const project = this.projects.get(projectId);
        this.projects.set(projectId, { ...project, count: (project?.count || 0) + 1, deviceInfos });
        return project ? Promise.resolve() : this.invokeJoinProject(projectId, deviceInfos);
    }

    static invokeJoinProject(projectId, deviceInfos) {
        return this.invoke('JoinProject', projectId, deviceInfos).then(result => {
            if (!result) return;
            this.emit('PresenceChanged', projectId, result.members);
            this.emit('LocksChanged', projectId, result.locks);
            this.sendHeartbeat(); // Qualité de la connexion visible des autres membres dès l'arrivée
            // Le serveur oublie l'activité quand la connexion quitte le groupe (reconnexion comprise)
            const activity = this.projects.get(projectId)?.activity;
            if (activity) this.invoke('SetActivity', projectId, activity);
        });
    }

    // Élément ouvert : { elementId, category, reference, mode: 'view' | 'edit' }, ou null. Ignoré pour un visiteur non collaborateur
    static setActivity(projectId, activity) {
        const project = this.projects.get(projectId);
        if (!project || JSON.stringify(project.activity || null) === JSON.stringify(activity || null)) return Promise.resolve();
        this.projects.set(projectId, { ...project, activity: activity || null });
        return this.invoke('SetActivity', projectId, activity || null);
    }

    static leaveProject(projectId) {
        const project = this.projects.get(projectId);
        if (!project) return Promise.resolve();
        if (project.count > 1) {
            this.projects.set(projectId, { ...project, count: project.count - 1 });
            return Promise.resolve();
        }
        this.projects.delete(projectId);
        this.presence.delete(projectId);
        return this.invoke('LeaveProject', projectId);
    }

    static getPresence = (projectId) => this.presence.get(projectId) || null;

    // Rejoint à nouveau un projet déjà suivi (ex. droits modifiés) pour recevoir la présence et les verrous à jour
    static refreshProject(projectId) {
        const project = this.projects.get(projectId);
        return project ? this.invokeJoinProject(projectId, project.deviceInfos) : Promise.resolve();
    }

    static lockElements(projectId, ids) {
        if (!ids?.length) return Promise.resolve();
        return this.invoke('LockElements', projectId, ids).then(locks => locks && this.emit('LocksChanged', projectId, locks));
    }

    static unlockElements(projectId, ids) {
        if (!ids?.length) return Promise.resolve();
        return this.invoke('UnlockElements', projectId, ids).then(locks => locks && this.emit('LocksChanged', projectId, locks));
    }

    // Formulaires et tableaux co-édités : les membres arrivent par FormMembersChanged, y compris pour l'appelant

    static joinForm(projectId, formKey) {
        const key = `${projectId}:${formKey}`;
        const form = this.forms.get(key);
        this.forms.set(key, { count: (form?.count || 0) + 1, projectId, formKey });
        return form ? Promise.resolve() : this.invokeJoinForm(projectId, formKey);
    }

    static invokeJoinForm(projectId, formKey) {
        // Nouveau groupe côté serveur (arrivée ou reconnexion) : le prochain envoi part complet
        this.formBaselines.delete(`${projectId}:${formKey}`);
        return this.invoke('JoinForm', projectId, formKey).then(members => {
            if (members) this.emit('FormMembersChanged', projectId, formKey, members);
        });
    }

    static leaveForm(projectId, formKey) {
        const key = `${projectId}:${formKey}`;
        const form = this.forms.get(key);
        if (!form) return Promise.resolve();
        if (form.count > 1) {
            this.forms.set(key, { ...form, count: form.count - 1 });
            return Promise.resolve();
        }
        this.forms.delete(key);
        this.formBaselines.delete(key);
        return this.invoke('LeaveForm', projectId, formKey);
    }

    // N'envoie que ce qui a changé depuis le dernier envoi (le premier envoi est complet) ; les destinataires fusionnent avec deepMerge.
    // isComplete : state est l'état entier (propriétés d'un formulaire), une clé disparue est alors envoyée à null.
    // Sinon state est un extrait (TreeForm, tableaux) : les clés absentes sont inchangées
    static updateForm(projectId, formKey, state, { isComplete = false } = {}) {
        if (!this.isConnected() || !state) return Promise.resolve(null);
        const key = `${projectId}:${formKey}`;
        const baseline = this.formBaselines.get(key);
        const copy = JSON.parse(JSON.stringify(state));
        const message = baseline ? this.diffState(baseline, copy, isComplete) : copy;
        if (!Object.keys(message).length) return Promise.resolve(null);

        this.formBaselines.set(key, baseline && !isComplete ? this.deepMerge(baseline, copy) : copy);
        return this.connection.invoke('UpdateForm', projectId, formKey, JSON.stringify(message)).catch(error => {
            console.error(error);
            this.formBaselines.delete(key); // Échec : rien ne garantit que les autres l'ont reçu
            return null;
        });
    }

    // Fusion à trois versions d'un état local avec une version serveur reçue ; base : version serveur sur laquelle repose l'état local.
    // Un champ modifié à distance et pas localement prend la valeur reçue ; modifié des deux côtés avec des valeurs différentes,
    // la saisie locale est gardée (conflit). Objets fusionnés clé par clé, tableaux et dates comparés entiers.
    // Renvoie { merged, changed, conflicts } (chemins des champs repris et des conflits)
    static mergeRemoteChanges(base, local, remote, path = '') {
        const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
        // Même instant écrit avec ou sans fuseau : pas une modification
        const asInstant = (value) => (typeof value === 'string' && ISO_DATE.test(value) ? Date.parse(/(Z|[+-]\d\d:\d\d)$/i.test(value) ? value : value + 'Z') : NaN);
        const isSame = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null) || asInstant(a) === asInstant(b);
        const result = { merged: { ...local }, changed: [], conflicts: [] };
        new Set([...Object.keys(base || {}), ...Object.keys(remote || {})]).forEach(key => {
            const baseValue = base?.[key], localValue = local?.[key], remoteValue = remote?.[key];
            if (isSame(baseValue, remoteValue)) return; // Inchangé à distance
            if (isObject(baseValue) && isObject(localValue) && isObject(remoteValue)) {
                const nested = this.mergeRemoteChanges(baseValue, localValue, remoteValue, `${path}${key}.`);
                result.merged[key] = nested.merged;
                result.changed.push(...nested.changed);
                result.conflicts.push(...nested.conflicts);
            } else if (isSame(baseValue, localValue)) {
                result.merged[key] = remoteValue;
                result.changed.push(path + key);
            } else if (!isSame(localValue, remoteValue)) result.conflicts.push(path + key);
        });
        return result;
    }

    // Formulaire d'élément ouvert : version serveur de référence (form.serverFeature / form.serverProperties), à poser au chargement.
    // toFormProperties transforme feature.properties comme le formulaire le fait pour son state.properties
    static trackServerFeature(form, feature, toFormProperties) {
        form.serverFeature = feature;
        form.serverProperties = feature ? toFormProperties(JSON.parse(JSON.stringify(feature.properties))) : null;
    }

    // Nouvelle version serveur de l'élément ouvert (enregistrée depuis un tableau, un autre formulaire…) : fusionnée dans la saisie en cours
    // au lieu d'être ignorée, pour qu'une validation ne réécrive pas l'ancienne version. À appeler depuis componentDidUpdate
    static syncFormWithServer(form, feature, toFormProperties) {
        if (!feature || !form.serverFeature || feature === form.serverFeature || feature.id !== form.serverFeature.id) return;
        // Version construite localement (enregistrement du formulaire lui-même) : nouvelle référence, rien à fusionner
        if (!this.remoteFeatures.has(feature)) return this.trackServerFeature(form, feature, toFormProperties);
        const base = form.serverProperties;
        this.trackServerFeature(form, feature, toFormProperties);
        // Sa propre saisie acceptée en retard : pas de toast pour les champs calculés ou remplis par le serveur
        const isOwnSync = this.syncedFeatures.has(feature);
        const isServerFill = (path) => SERVER_COMPUTED_PROPERTIES.includes(path) || [null, undefined, ''].includes(path.split('.').reduce((value, key) => value?.[key], base));
        const { merged, changed, conflicts } = this.mergeRemoteChanges(base, form.state.properties, form.serverProperties);
        if (changed.length) form.setState({ properties: merged });
        if (conflicts.length) showToast('element_remote_conflict');
        else if (changed.length && !(isOwnSync && changed.every(isServerFill))) showToast('element_remote_update');
    }

    // Différence récursive au format de deepMerge : tableaux et customFields entiers, objets par clé
    static diffState(baseline, state, isComplete) {
        const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);
        const diff = {};
        Object.keys(state).forEach(key => {
            const value = state[key];
            const baselineValue = baseline?.[key];
            if (key !== 'customFields' && isObject(value) && isObject(baselineValue)) {
                const nestedDiff = this.diffState(baselineValue, value, isComplete);
                if (Object.keys(nestedDiff).length) diff[key] = nestedDiff;
            } else if (JSON.stringify(value) !== JSON.stringify(baselineValue)) diff[key] = value;
        });
        if (isComplete && isObject(baseline))
            Object.keys(baseline).forEach(key => {
                if (!(key in state) && baseline[key] !== null) diff[key] = null;
            });
        return diff;
    }

    // Renvoie true si un autre membre a été sollicité (sa réponse arrive par FormUpdated)
    static requestFormState(projectId, formKey) {
        return this.invoke('RequestFormState', projectId, formKey).then(asked => !!asked);
    }

    static sendFormState(connectionId, projectId, formKey, state) {
        return this.invoke('SendFormState', connectionId, projectId, formKey, JSON.stringify(state));
    }

    // Fusion d'un état de formulaire reçu dans l'état local
    static deepMerge(target, source) {
        if (!source)
            return target;

        const output = { ...target };

        Object.keys(source).forEach(key => {
            const sourceValue = source[key];
            const targetValue = target?.[key];

            // SPECIAL CASE:
            // customFields must replace completely
            if (key === 'customFields') {
                output[key] = sourceValue;
                return;
            }

            // Arrays => replace completely
            if (Array.isArray(sourceValue)) {
                output[key] = sourceValue;
            }

            // Recursive merge
            else if (
                sourceValue &&
                typeof sourceValue === 'object' &&
                !(sourceValue instanceof Date)
            ) {
                output[key] = this.deepMerge(
                    targetValue || {},
                    sourceValue
                );
            }

            // Primitive values
            else {
                output[key] = sourceValue;
            }
        });

        return output;
    }
}
